import SettingsCheckbox from "~/profile/utils/checkbox";
import { prepareSetting, setUserSettings } from "~/util/providers/mapybox";
import { ESETTINGS_KEYS, IUserInitialPosition, IUserSpeedData } from "~/state/user/interfaces";
import { store } from "~/state/store";
import { icon, node } from "~/util/dom";
import { setSetting } from "~/state/user/state";
import SettingsMapsets from "~/profile/settings/mapsets";
import SettingsInitialPosition from "~/profile/settings/initialposition";
import SettingsUserSpeed from "~/profile/settings/userspeed";
import { _ } from "~/i18n";
import { MAP_OVERLAYS } from "~/state/map/constants";
import type Settings from "~/settings";
import { Unsubscribe } from "redux";
import { watch, watchUser, watchPremiumData } from "~/state/util";

export default class ProfileSettings {
	_comp: Settings;

	_container: HTMLDivElement;

	_list: HTMLDivElement;

	_mapsets: SettingsMapsets;

	_initialPosition: SettingsInitialPosition;

	_userSpeed: SettingsUserSpeed;

	_checkboxes: Array<SettingsCheckbox>;

	_storeUnsubscribeUser: Unsubscribe;

	_storeUnsubscribePremium: Unsubscribe;

	constructor(comp: Settings) {
		const backBtnLink = node("a", { className: "back-btn-link" });
		const backBtn = node("button", { className: "back-btn" });

		this._comp = comp;
		this._container = node("div", { className: "userprofile-settings" });
		this._list = node("div", { className: "settings-list" });
		this._container.appendChild(backBtnLink);
		this._container.appendChild(node("h2", { className: "title", textContent: _("profile.settings.title") }));
		this._container.appendChild(this._list);
		backBtn.appendChild(icon("close-thin"));
		backBtnLink.appendChild(backBtn);
		backBtnLink.addEventListener("click", event => {
			event.preventDefault();
			this._comp.deactivate();
		});
	}

	get container() {
		return this._container;
	}

	activate() {
		this._list.innerHTML = "";

		const { user } = store.getState();
		const settings = user.settings;

		// uvodni souradnice mapy
		const coords = settings.find(item => item.title === ESETTINGS_KEYS.MAP_POSITION_WEB_DEFAULT);
		const coordsDataValue = coords?.data?.value as IUserInitialPosition;

		this._initialPosition = new SettingsInitialPosition({
			onChange: async (position: IUserInitialPosition) => {
				const setting = prepareSetting(ESETTINGS_KEYS.MAP_POSITION_WEB_DEFAULT, position);

				await setUserSettings(setting);
				this._initialPosition.mapCoords = setting.data.value as IUserInitialPosition;
				store.dispatch(setSetting(setting));
			},
			mapCoords: coordsDataValue && Object.keys(coordsDataValue).length ? coordsDataValue : null,
		});
		this._list.appendChild(this._initialPosition.container);

		// nastaveni uvodniho mapsetu
		const mapsetWebDefaultId = settings.find(item => item.title === ESETTINGS_KEYS.MAPSET_WEB_DEFAULT_ID);

		this._mapsets = new SettingsMapsets({
			selectedId: (mapsetWebDefaultId?.data?.value || 0) as number,
			onChange: (id: number, seo: string) => {
				const setting = prepareSetting(ESETTINGS_KEYS.MAPSET_WEB_DEFAULT_ID, id);
				// pri zmene defaultu nulujeme zapamatovane overlaye
				const overlays = prepareSetting(ESETTINGS_KEYS.MAPSET_WEB_OVERLAYS, seo === "zimni" ? [MAP_OVERLAYS.SKI] : []);

				setUserSettings([setting, overlays]);
				store.dispatch(setSetting(setting));

				Mapy.getComponent("logger").log("mapset_default_set", {
					section: "accounts",
					type: seo,
				});
			},
		});
		this._list.appendChild(this._mapsets.container);

		// nastaveni aktivnich zadosti o hodnoceni
		const reviewNotificationsEnabled = settings.find(item => item.title === ESETTINGS_KEYS.REVIEW_NOTIFICATIONS_ENABLED);
		const searchMapSuppressionEnabled = settings.find(item => item.title === ESETTINGS_KEYS.SEARCH_MAP_SUPPRESSION);

		this._checkboxes = [new SettingsCheckbox({
			title: _("profile.settings.useReviews.title"),
			desc: _("profile.settings.useReviews.desc"),
			checked: !!reviewNotificationsEnabled?.data?.value || false,
			change: checked => {
				const setting = prepareSetting(ESETTINGS_KEYS.REVIEW_NOTIFICATIONS_ENABLED, !!checked);

				setUserSettings(setting);
				store.dispatch(setSetting(setting));
				// analytika
				Mapy.getComponent("logger").log(`suggestion_reviews_${checked ? "on" : "off"}`, {
					section: "accounts",
				});
			},
		}), new SettingsCheckbox({
			title: _("profile.settings.searchSuppression.title"),
			desc: _("profile.settings.searchSuppression.desc"),
			checked: !!searchMapSuppressionEnabled?.data?.value || false,
			change: checked => {
				const setting = prepareSetting(ESETTINGS_KEYS.SEARCH_MAP_SUPPRESSION, !!checked);

				setUserSettings(setting);
				store.dispatch(setSetting(setting));
				// analytika
				/*Mapy.getComponent("logger").log(`suggestion_reviews_${checked ? "on" : "off"}`, {
					section: "accounts",
				});*/
			},
		})];
		this._checkboxes.forEach(checkbox => {
			this._list.appendChild(checkbox.container);
		});

		this._userSpeed = new SettingsUserSpeed({
			title: _("profile.settings.userspeed.title"),
			desc: _("profile.settings.userspeed.description"),
			change: userSpeedData => {
				const setting = prepareSetting(ESETTINGS_KEYS.ROUTING_CRITERION_SPEED, userSpeedData);

				setUserSettings(setting);
				store.dispatch(setSetting(setting));
			},
		}, (settings.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value) as IUserSpeedData || {});

		this._list.appendChild(this._userSpeed.container);

		// zavrit pri zmene uzivatele
		const wtUser = watch(store.getState, "user", watchUser);

		this._storeUnsubscribeUser = store.subscribe(wtUser(() => {
			this._comp.deactivate();
		}));

		// zavrit pri zmene premia - muze byt jine nastaveni a tak radeji nechat uzivatele znovu otevrit
		const wtUserPremium = watch(store.getState, "user", watchPremiumData);

		this._storeUnsubscribePremium = store.subscribe(wtUserPremium(() => {
			this._comp.deactivate();
		}));
	}

	deactivate() {
		this._mapsets && this._mapsets.destroy();
		this._mapsets = null;

		this._userSpeed && this._userSpeed.destroy();
		this._userSpeed = null;

		while (this._checkboxes.length) {
			const checkbox = this._checkboxes.shift();

			checkbox.destroy();
		}

		this._checkboxes = [];

		this._storeUnsubscribeUser();
		this._storeUnsubscribePremium();
	}

	// eslint-disable-next-line class-methods-use-this
	getState() {
		return {};
	}

	// eslint-disable-next-line class-methods-use-this
	setState() {}
}
